import React, { useState, useRef, useEffect } from "react";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import ZoomInMapIcon from "@mui/icons-material/ZoomInMap";
import HTMLFlipBook from "react-pageflip";
import { motion } from "framer-motion";
import { useTheme } from "@mui/material/styles";
import booksData from "../data/ebook";
import { useTranslation } from "react-i18next";

const Ebook = () => {
  const [open, setOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Responsive flipbook sizing
  const viewerRef = useRef(null);
  const [viewerWidth, setViewerWidth] = useState(600);
  const { t } = useTranslation();
  const ebook = t("ebook", { returnObjects: true });

  useEffect(() => {
    const updateSize = () => {
      const container = viewerRef.current;
      const baseWidth = isFullscreen
        ? Math.min(window.innerWidth * 0.9, 1100)
        : Math.min(window.innerWidth * 0.7, 800);
      setViewerWidth(baseWidth);
    };

    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, [isFullscreen]);

  const A4_RATIO = 1.4142;
  const flipWidth = isMobile ? Math.min(viewerWidth, 360) : viewerWidth;
  const flipHeight = Math.round(flipWidth * A4_RATIO);

  const handleOpen = (book) => {
    setSelectedBook(book);
    setOpen(true);
    setIsFullscreen(false);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedBook(null);
  };

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  return (
    <Box className="ebook-section">
      <Container>
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Typography  variant="h3" className="ebook-title" sx={{ color: "#FFD700", textAlign: "center", mb: 1 }}>
            {t("ebook.title")}
          </Typography>
          <Typography variant="subtitle1" className="ebook-subtitle" sx={{ color: "#f5e8c7", textAlign: "center", mb: 5 }}>
            {t("ebook.description")}
          </Typography>
        </motion.div>

        {/* Books Grid */}
        <Grid container spacing={4} className="ebook-grid" justifyContent="center" >
          {booksData.map((book, index) => (
            <Grid item xs={12} sm={6} md={4} key={book.id}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.2 }}
              >
                <Card
                  sx={{
                    backgroundColor: "#1c1c1c",
                    color: "#fff",
                    borderRadius: "16px",
                    boxShadow: "0 0 15px rgba(255,215,0,0.2)",
                    transition: "transform 0.3s",
                    "&:hover": { transform: "scale(1.03)" },
                  }}
                >
                  <CardMedia component="img" image={book.cover} alt={book.title} sx={{  objectFit: "fill !important",
                    height: "450px !important",
                    width: "330px !important", }} />
                  <CardContent>
                    <Typography variant="h6" sx={{ color: "#FFD700" }}>
                      {book.title}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
                      {book.author}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                      {book.description}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: "center", pb: 2 }}>
                    <Button
                      variant="contained"
                      onClick={() => handleOpen(book)}
                      sx={{
                        backgroundColor: "#FFD700",
                        color: "#000",
                        fontWeight: "600",
                        "&:hover": { backgroundColor: "#ffea70" },
                      }}
                    >
                      Read Now
                    </Button>
                  </CardActions>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Flip-Book Dialog */}
        <Dialog
          open={open}
          onClose={handleClose}
          fullWidth
          maxWidth="lg"
          PaperProps={{
            sx: {
              backgroundColor: "#000",
              color: "#fff",
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: "0 0 30px rgba(255, 215, 0, 0.5)",
            },
          }}
        >
          <DialogTitle
            sx={{
              textAlign: "center",
              fontWeight: 600,
              color: "#FFD700",
              fontSize: "1.5rem",
            }}
          >
            {selectedBook?.title}
            <IconButton
              onClick={handleClose}
              sx={{ position: "absolute", right: 8, top: 8, color: "#FFD700" }}
            >
              <CloseIcon />
            </IconButton>

            {/* <Tooltip title={isFullscreen ? "Minimize" : "Maximize"} arrow>
              <IconButton
                onClick={toggleFullscreen}
                sx={{
                  position: "absolute",
                  right: 50,
                  top: 8,
                  color: "#FFD700",
                }}
              >
                {isFullscreen ? <ZoomInMapIcon /> : <ZoomOutMapIcon />}
              </IconButton>
            </Tooltip> */}
          </DialogTitle>

          <DialogContent
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "#000",
              py: 3,
              transition: "all 0.4s ease",
            }}
          >
            <div
              ref={viewerRef}
              style={{
                width: isFullscreen ? "90vw" : "70vw",
                display: "flex",
                justifyContent: "center",
                transition: "width 0.4s ease",
              }}
            >
              {selectedBook && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.45 }}
                >
                  <HTMLFlipBook
                    width={flipWidth}
                    height={flipHeight}
                    size="stretch"
                    minWidth={isMobile ? 280 : 400}
                    maxWidth={isMobile ? 500 : 1200}
                    minHeight={isMobile ? 400 : 560}
                    maxHeight={isMobile ? 900 : 1600}
                    showCover
                    drawShadow
                    maxShadowOpacity={0.4}
                    flippingTime={700}
                    usePortrait={isMobile}
                    mobileScrollSupport
                    style={{
                      margin: "auto",
                      backgroundColor: "#111",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.6)",
                      borderRadius: "10px",
                      overflow: "hidden",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {selectedBook.pages.map((page, index) => (
                      <div
                        key={index}
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          backgroundColor: "#fff",
                          padding: 12,
                          boxSizing: "border-box",
                        }}
                      >
                        <img
                          src={page}
                          alt={`Page ${index + 1}`}
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                            borderRadius: 4,
                            backgroundColor: "#fff",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                          }}
                        />
                      </div>
                    ))}
                  </HTMLFlipBook>
                </motion.div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </Container>
    </Box>
  );
};

export default Ebook;
